"use client"

import type React from "react"
import { useCallback, useMemo, useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { toast } from "sonner"
import { Badge } from "@/components/ui/badge"
import {
  Copy,
  Download,
  Upload,
  Info,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Code,
  Zap
} from "lucide-react"

interface RegexMatch {
  match: string
  index: number
  groups: string[]
}

interface HighlightSegment {
  key: string
  text: string
  isMatch: boolean
}

interface CommonPattern {
  name: string
  pattern: string
  description: string
  example: string
}

const COMMON_PATTERNS: CommonPattern[] = [
  {
    name: "Email",
    pattern: "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$",
    description: "Matches email addresses",
    example: "user@example.com"
  },
  {
    name: "URL",
    pattern: "^(https?:\\/\\/)?(www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b([-a-zA-Z0-9()@:%_\\+.~#?&\\/\\/=]*)$",
    description: "Matches URLs",
    example: "https://www.example.com"
  },
  {
    name: "Phone (US)",
    pattern: "^\\(?([0-9]{3})\\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$",
    description: "Matches US phone numbers",
    example: "(555) 123-4567"
  },
  {
    name: "IPv4 Address",
    pattern: "^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$",
    description: "Matches IPv4 addresses",
    example: "192.168.1.1"
  },
  {
    name: "Date (YYYY-MM-DD)",
    pattern: "^\\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$",
    description: "Matches dates in YYYY-MM-DD format",
    example: "2024-03-15"
  },
  {
    name: "Time (HH:MM)",
    pattern: "^([01]?[0-9]|2[0-3]):[0-5][0-9]$",
    description: "Matches time in 24-hour format",
    example: "14:30"
  },
  {
    name: "Hex Color",
    pattern: "^#?([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$",
    description: "Matches hex color codes",
    example: "#FF5733"
  },
  {
    name: "Username",
    pattern: "^[a-zA-Z0-9_-]{3,16}$",
    description: "Alphanumeric with _ and -, 3-16 chars",
    example: "john_doe123"
  },
  {
    name: "Password (Strong)",
    pattern: "^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]{8,}$",
    description: "Min 8 chars, uppercase, lowercase, number, special char",
    example: "Pass123@word"
  },
  {
    name: "Credit Card",
    pattern: "^(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|3[47][0-9]{13}|6(?:011|5[0-9]{2})[0-9]{12})$",
    description: "Matches common credit card numbers",
    example: "4532015112830366"
  }
]

export function RegexTester() {
  const [pattern, setPattern] = useState("")
  const [testString, setTestString] = useState("")
  const [flags, setFlags] = useState({
    global: true,
    caseInsensitive: false,
    multiline: false,
    dotAll: false,
    unicode: false,
    sticky: false
  })
  const [selectedPattern, setSelectedPattern] = useState<string>("")
  const [replaceWith, setReplaceWith] = useState("")

  const getFlagsString = useCallback(() => {
    let flagStr = ""
    if (flags.global) flagStr += "g"
    if (flags.caseInsensitive) flagStr += "i"
    if (flags.multiline) flagStr += "m"
    if (flags.dotAll) flagStr += "s"
    if (flags.unicode) flagStr += "u"
    if (flags.sticky) flagStr += "y"
    return flagStr
  }, [flags])

  const { matches, isValid, errorMessage, highlightedSegments, replacedText } = useMemo(() => {
    if (!pattern) {
      return {
        matches: [] as RegexMatch[],
        isValid: true,
        errorMessage: "",
        highlightedSegments: testString
          ? [{ key: "text-all", text: testString, isMatch: false }]
          : [] as HighlightSegment[],
        replacedText: "",
      }
    }

    try {
      const regex = new RegExp(pattern, getFlagsString())
      const foundMatches: RegexMatch[] = []
      let result: RegExpExecArray | null
      let lastIndex = 0
      const segments: HighlightSegment[] = []

      if (flags.global) {
        while ((result = regex.exec(testString)) !== null) {
          foundMatches.push({
            match: result[0],
            index: result.index,
            groups: result.slice(1),
          })

          if (result.index > lastIndex) {
            segments.push({
              key: `text-${lastIndex}`,
              text: testString.substring(lastIndex, result.index),
              isMatch: false,
            })
          }

          segments.push({
            key: `match-${result.index}`,
            text: result[0],
            isMatch: true,
          })

          lastIndex = result.index + result[0].length
          if (result[0].length === 0) regex.lastIndex++
        }

        if (lastIndex < testString.length) {
          segments.push({
            key: "text-end",
            text: testString.substring(lastIndex),
            isMatch: false,
          })
        }
      } else {
        result = regex.exec(testString)
        if (result) {
          foundMatches.push({
            match: result[0],
            index: result.index,
            groups: result.slice(1),
          })
          segments.push(
            {
              key: "text-start",
              text: testString.substring(0, result.index),
              isMatch: false,
            },
            { key: `match-${result.index}`, text: result[0], isMatch: true },
            {
              key: "text-end",
              text: testString.substring(result.index + result[0].length),
              isMatch: false,
            }
          )
        } else {
          segments.push({ key: "text-all", text: testString, isMatch: false })
        }
      }

      const replaceRegex = new RegExp(pattern, getFlagsString())
      return {
        matches: foundMatches,
        isValid: true,
        errorMessage: "",
        highlightedSegments: segments,
        replacedText: testString.replace(replaceRegex, replaceWith),
      }
    } catch (regexError) {
      return {
        matches: [] as RegexMatch[],
        isValid: false,
        errorMessage: regexError instanceof Error ? regexError.message : "Invalid regular expression",
        highlightedSegments: testString
          ? [{ key: "text-all", text: testString, isMatch: false }]
          : [] as HighlightSegment[],
        replacedText: "",
      }
    }
  }, [pattern, testString, flags.global, getFlagsString, replaceWith])

  const highlightedText = highlightedSegments.map((segment) =>
    segment.isMatch ? (
      <mark
        key={segment.key}
        className="bg-yellow-200 dark:bg-yellow-900 px-0.5 rounded"
      >
        {segment.text}
      </mark>
    ) : (
      <span key={segment.key}>{segment.text}</span>
    )
  )

  const handlePatternSelect = (patternName: string) => {
    const selected = COMMON_PATTERNS.find(p => p.name === patternName)
    if (selected) {
      setPattern(selected.pattern)
      setTestString(selected.example)
      setSelectedPattern(patternName)
    }
  }

  const handleCopyPattern = () => {
    navigator.clipboard.writeText(pattern)
    toast.success("Regex pattern copied to clipboard!")
  }

  const handleCopyCode = (language: string) => {
    let code = ""
    const flagStr = getFlagsString()

    switch(language) {
      case "javascript":
        code = `const regex = /${pattern}/${flagStr};\nconst matches = testString.match(regex);`
        break
      case "python":
        const pythonFlags = []
        if (flags.caseInsensitive) pythonFlags.push("re.IGNORECASE")
        if (flags.multiline) pythonFlags.push("re.MULTILINE")
        if (flags.dotAll) pythonFlags.push("re.DOTALL")
        const flagsCode = pythonFlags.length > 0 ? `, ${pythonFlags.join(" | ")}` : ""
        code = `import re\n\npattern = r"${pattern}"\nmatches = re.findall(pattern, test_string${flagsCode})`
        break
      case "java":
        const javaFlags = []
        if (flags.caseInsensitive) javaFlags.push("Pattern.CASE_INSENSITIVE")
        if (flags.multiline) javaFlags.push("Pattern.MULTILINE")
        if (flags.dotAll) javaFlags.push("Pattern.DOTALL")
        const javaflagsCode = javaFlags.length > 0 ? ` | ${javaFlags.join(" | ")}` : ""
        code = `import java.util.regex.*;\n\nPattern pattern = Pattern.compile("${pattern}"${javaflagsCode ? `, ${javaflagsCode}` : ""});\nMatcher matcher = pattern.matcher(testString);`
        break
      case "php":
        code = `$pattern = '/${pattern}/${flagStr}';\npreg_match_all($pattern, $testString, $matches);`
        break
    }

    navigator.clipboard.writeText(code)
    toast.success(`${language} code copied to clipboard!`)
  }

  const handleExport = () => {
    const exportData = {
      pattern,
      flags: getFlagsString(),
      testString,
      matches,
      timestamp: new Date().toISOString()
    }

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `regex-test-${Date.now()}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)

    toast.success("Test data exported to JSON file")
  }

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target?.result as string)
        setPattern(data.pattern || "")
        setTestString(data.testString || "")

        // Parse flags
        const importedFlags = data.flags || ""
        setFlags({
          global: importedFlags.includes("g"),
          caseInsensitive: importedFlags.includes("i"),
          multiline: importedFlags.includes("m"),
          dotAll: importedFlags.includes("s"),
          unicode: importedFlags.includes("u"),
          sticky: importedFlags.includes("y")
        })

        toast.success("Test data imported from file")
      } catch {
        toast.error("Import failed: Invalid file format")
      }
    }
    reader.readAsText(file)
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Regular Expression Tester</CardTitle>
          <CardDescription>
            Test and debug regular expressions with real-time matching and visualization
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pattern">Regular Expression Pattern</Label>
              <div className="flex gap-2">
                <Input
                  id="pattern"
                  placeholder="Enter your regex pattern (e.g., [a-z]+)"
                  value={pattern}
                  onChange={(e) => setPattern(e.target.value)}
                  className={`font-mono ${!isValid ? "border-red-500" : ""}`}
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCopyPattern}
                  disabled={!pattern}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              {!isValid && (
                <div className="flex items-center gap-2 text-sm text-red-500">
                  <XCircle className="h-4 w-4" />
                  {errorMessage}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Common Patterns</Label>
              <Select value={selectedPattern} onValueChange={handlePatternSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a common pattern" />
                </SelectTrigger>
                <SelectContent>
                  {COMMON_PATTERNS.map((p) => (
                    <SelectItem key={p.name} value={p.name}>
                      <div className="flex flex-col">
                        <span>{p.name}</span>
                        <span className="text-xs text-muted-foreground">{p.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Regex Flags</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="global"
                    checked={flags.global}
                    onCheckedChange={(checked) => setFlags({ ...flags, global: checked })}
                  />
                  <Label htmlFor="global" className="cursor-pointer">
                    <Badge variant="outline">g</Badge> Global
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="caseInsensitive"
                    checked={flags.caseInsensitive}
                    onCheckedChange={(checked) => setFlags({ ...flags, caseInsensitive: checked })}
                  />
                  <Label htmlFor="caseInsensitive" className="cursor-pointer">
                    <Badge variant="outline">i</Badge> Case Insensitive
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="multiline"
                    checked={flags.multiline}
                    onCheckedChange={(checked) => setFlags({ ...flags, multiline: checked })}
                  />
                  <Label htmlFor="multiline" className="cursor-pointer">
                    <Badge variant="outline">m</Badge> Multiline
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="dotAll"
                    checked={flags.dotAll}
                    onCheckedChange={(checked) => setFlags({ ...flags, dotAll: checked })}
                  />
                  <Label htmlFor="dotAll" className="cursor-pointer">
                    <Badge variant="outline">s</Badge> Dot All
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="unicode"
                    checked={flags.unicode}
                    onCheckedChange={(checked) => setFlags({ ...flags, unicode: checked })}
                  />
                  <Label htmlFor="unicode" className="cursor-pointer">
                    <Badge variant="outline">u</Badge> Unicode
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="sticky"
                    checked={flags.sticky}
                    onCheckedChange={(checked) => setFlags({ ...flags, sticky: checked })}
                  />
                  <Label htmlFor="sticky" className="cursor-pointer">
                    <Badge variant="outline">y</Badge> Sticky
                  </Label>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="testString">Test String</Label>
              <Textarea
                id="testString"
                placeholder="Enter text to test against the pattern"
                value={testString}
                onChange={(e) => setTestString(e.target.value)}
                className="min-h-[120px] md:min-h-[150px] font-mono text-sm"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="replaceWith">Replace With (optional)</Label>
              <Input
                id="replaceWith"
                placeholder="Replacement string (supports $1, $2 for groups)"
                value={replaceWith}
                onChange={(e) => setReplaceWith(e.target.value)}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Use $1, $2, etc. for capture group references. $& for full match.
              </p>
            </div>
          </div>

          <Tabs defaultValue="matches" className="w-full">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-4">
              <TabsTrigger value="matches">
                Matches ({matches.length})
              </TabsTrigger>
              <TabsTrigger value="highlight">Highlighted</TabsTrigger>
              <TabsTrigger value="replace">Replace</TabsTrigger>
              <TabsTrigger value="code">Code</TabsTrigger>
            </TabsList>

            <TabsContent value="matches" className="space-y-4">
              {matches.length > 0 ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                    <CheckCircle2 className="h-4 w-4" />
                    Found {matches.length} match{matches.length !== 1 ? "es" : ""}
                  </div>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted">
                        <tr>
                          <th className="text-left p-2 font-medium">Match #</th>
                          <th className="text-left p-2 font-medium">Value</th>
                          <th className="text-left p-2 font-medium">Position</th>
                          <th className="text-left p-2 font-medium">Groups</th>
                        </tr>
                      </thead>
                      <tbody>
                        {matches.map((match, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="p-2">{idx + 1}</td>
                            <td className="p-2 font-mono bg-yellow-50 dark:bg-yellow-900/20">
                              {match.match}
                            </td>
                            <td className="p-2">{match.index}</td>
                            <td className="p-2">
                              {match.groups.length > 0 ? (
                                <div className="flex gap-1 flex-wrap">
                                  {match.groups.map((group, gIdx) => (
                                    <Badge key={gIdx} variant="secondary">
                                      Group {gIdx + 1}: {group || "(empty)"}
                                    </Badge>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-muted-foreground">No groups</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <AlertCircle className="h-4 w-4" />
                  {pattern ? "No matches found" : "Enter a pattern to start testing"}
                </div>
              )}
            </TabsContent>

            <TabsContent value="highlight" className="space-y-4">
              <div className="p-4 bg-muted rounded-lg font-mono text-sm whitespace-pre-wrap break-all">
                {highlightedText.length > 0 ? highlightedText : <span className="text-muted-foreground">No text to display</span>}
              </div>
            </TabsContent>

            <TabsContent value="replace" className="space-y-4">
              {replacedText && pattern ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                    <span>
                      Replaced {matches.length} match{matches.length !== 1 ? "es" : ""} with &quot;{replaceWith}&quot;
                    </span>
                  </div>
                  <div className="p-4 bg-muted rounded-lg font-mono text-sm whitespace-pre-wrap break-all">
                    {replacedText}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(replacedText)
                      toast.success("Replaced text copied to clipboard!")
                    }}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Result
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <AlertCircle className="h-4 w-4" />
                  {!pattern ? "Enter a pattern and replacement string" : "Enter a replacement string above"}
                </div>
              )}
            </TabsContent>

            <TabsContent value="code" className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <Button variant="outline" onClick={() => handleCopyCode("javascript")}>
                  <Code className="h-4 w-4 mr-2" />
                  JavaScript
                </Button>
                <Button variant="outline" onClick={() => handleCopyCode("python")}>
                  <Code className="h-4 w-4 mr-2" />
                  Python
                </Button>
                <Button variant="outline" onClick={() => handleCopyCode("java")}>
                  <Code className="h-4 w-4 mr-2" />
                  Java
                </Button>
                <Button variant="outline" onClick={() => handleCopyCode("php")}>
                  <Code className="h-4 w-4 mr-2" />
                  PHP
                </Button>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">
                  Click a language button above to copy the regex code to your clipboard
                </p>
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex justify-between pt-4 border-t">
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleExport}>
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
              <div className="relative">
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImport}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                />
                <Button variant="outline" size="sm">
                  <Upload className="h-4 w-4 mr-2" />
                  Import
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isValid && pattern && (
                <Badge variant="outline" className="gap-1">
                  <Zap className="h-3 w-3" />
                  Pattern: /{pattern}/{getFlagsString()}
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-5 w-5" />
            Quick Reference
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <div>
              <h4 className="font-medium mb-2">Character Classes</h4>
              <div className="space-y-1 font-mono text-xs">
                <div><span className="text-primary">.</span> - Any character</div>
                <div><span className="text-primary">\d</span> - Digit (0-9)</div>
                <div><span className="text-primary">\w</span> - Word character</div>
                <div><span className="text-primary">\s</span> - Whitespace</div>
                <div><span className="text-primary">[abc]</span> - Character set</div>
                <div><span className="text-primary">[^abc]</span> - Negated set</div>
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-2">Quantifiers</h4>
              <div className="space-y-1 font-mono text-xs">
                <div><span className="text-primary">*</span> - 0 or more</div>
                <div><span className="text-primary">+</span> - 1 or more</div>
                <div><span className="text-primary">?</span> - 0 or 1</div>
                <div><span className="text-primary">{`{n}`}</span> - Exactly n</div>
                <div><span className="text-primary">{`{n,}`}</span> - n or more</div>
                <div><span className="text-primary">{`{n,m}`}</span> - Between n and m</div>
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-2">Anchors & Groups</h4>
              <div className="space-y-1 font-mono text-xs">
                <div><span className="text-primary">^</span> - Start of string</div>
                <div><span className="text-primary">$</span> - End of string</div>
                <div><span className="text-primary">\b</span> - Word boundary</div>
                <div><span className="text-primary">(abc)</span> - Capture group</div>
                <div><span className="text-primary">(?:abc)</span> - Non-capture</div>
                <div><span className="text-primary">a|b</span> - Alternation</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}